import {defineSlotRecipe} from '@chakra-ui/react3'

export const menuSlotRecipe = defineSlotRecipe({
    className: 'chakra-menu',
    slots: [
        'arrow',
        'arrowTip',
        'content',
        'contextTrigger',
        'indicator',
        'item',
        'itemGroup',
        'itemGroupLabel',
        'itemIndicator',
        'itemText',
        'positioner',
        'separator',
        'trigger',
        'triggerItem',
        'itemCommand'
    ],
    base: {
        content: {
            marginTop: 'spacing-16',
            // The precise value aligns the menu edge with the header edge.
            marginInlineEnd: '-7.3125rem',
            outline: 0,
            backgroundColor: 'background.actions.on-media.white',
            boxShadow: 'none',
            color: 'text.primary',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            maxHeight: 'var(--available-height)',
            '--menu-z-index': 'zIndex.dropdown',
            zIndex: 'calc(var(--menu-z-index) + var(--layer-index, 0))',
            borderRadius: 'l',
            border: '1px solid',
            borderColor: 'border.subtle',
            overflow: 'hidden',
            overflowY: 'auto',
            _open: {
                animationStyle: 'slide-fade-in',
                animationDuration: 'fast'
            },
            _closed: {
                animationStyle: 'slide-fade-out',
                animationDuration: 'faster'
            }
        },
        item: {
            textDecoration: 'none',
            color: 'text.primary',
            userSelect: 'none',
            width: '100%',
            display: 'flex',
            cursor: 'pointer',
            alignItems: 'center',
            textAlign: 'start',
            position: 'relative',
            flex: '0 0 auto',
            alignSelf: 'stretch',
            outline: 0,
            // items rendered as plain navigation links never carry Chakra's
            // roving highlight, so hover reaches the same treatment
            '&:is([data-highlighted], :hover)': {
                borderRadius: 's',
                '& [data-hover-end-element]': {
                    opacity: 1,
                    transform: 'translateX(0)'
                }
            },
            _focusVisible: {
                borderRadius: 's',
                outline: '1px solid',
                outlineOffset: '1px',
                outlineColor: 'border.strong',
                '& [data-hover-end-element]': {
                    opacity: 1,
                    transform: 'translateX(0)'
                }
            },
            _icon: {
                color: 'icons.primary'
            },

            _disabled: {
                pointerEvents: 'none',
                opacity: '0.5'
            }
        },
        itemText: {
            flex: '1',
            minW: '0'
        },
        itemIndicator: {
            position: 'absolute',
            insetStart: 'spacing-4',
            transform: 'translateY(-50%)',
            top: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'icons.primary'
        },
        itemGroupLabel: {
            p: 'spacing-8'
        },
        indicator: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: '0',
            color: 'icons.primary'
        },
        itemCommand: {
            display: 'inline-flex',
            alignItems: 'center',
            color: 'icons.primary',
            '&[data-hover-end-element]': {
                opacity: 0,
                transform: 'translateX(-4px)',
                transitionProperty: 'opacity, transform',
                transitionDuration: '0.2s',
                transitionTimingFunction: 'ease'
            }
        },
        separator: {
            height: '1px',
            bg: 'border.subtle',
            my: '0',
            mx: 'spacing-12',
            // Menu separator receives a default border from the rendered separator element
            // remove it so the 1px line comes only from the background above
            border: 'none'
        }
    },
    variants: {
        variant: {
            subtle: {
                item: {
                    '&:is([data-highlighted], :hover)': {
                        backgroundColor: 'background.section.subtle'
                    },
                    _focusVisible: {
                        backgroundColor: 'background.section.subtle'
                    }
                }
            }
        },
        size: {
            md: {
                content: {
                    textStyle: 'body.small',
                    minW: '24rem',
                    padding: 'spacing-12',
                    scrollPadding: 'spacing-12',
                    gap: 'spacing-4'
                },
                item: {
                    textStyle: 'body.small',
                    minH: 'spacing-48',
                    paddingInlineStart: 'spacing-12',
                    paddingInlineEnd: 'spacing-16',
                    py: 'spacing-12',
                    gap: 'spacing-8'
                }
            }
        }
    },
    defaultVariants: {
        size: 'md',
        variant: 'subtle'
    }
})
